import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { LdapManagedUser } from 'shared';
import { ldapUsersApi } from '../../../api/ldapUsers';
import { WizardDraft } from '../LdapUserWizard';

const UID_PATTERN = /^[a-zA-Z0-9._-]{2,50}$/;

const basicsSchema = z.object({
  givenName: z.string().trim().min(1, 'First name is required').max(100),
  sn: z.string().trim().min(1, 'Last name is required').max(100),
  mail: z.string().trim().email('Invalid email address').max(254),
  uid: z
    .string()
    .regex(UID_PATTERN, '2-50 characters: letters, numbers, . _ -'),
});

export type BasicsData = z.infer<typeof basicsSchema>;

type UidStatus = 'idle' | 'checking' | 'available' | 'taken-ldap' | 'taken-db' | 'invalid';

const FOLD_MAP: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

function suggestUid(givenName: string, sn: string): string {
  const raw = `${givenName.charAt(0)}${sn}`.toLowerCase();
  return raw
    .replace(/[äöüß]/g, (c) => FOLD_MAP[c])
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9._-]/g, '');
}

interface BasicsStepProps {
  draft: WizardDraft;
  users: LdapManagedUser[];
  templateLocked: boolean;
  onApplyTemplate: (user: LdapManagedUser | null) => void;
  onNext: (data: BasicsData) => void;
  onCancel: () => void;
}

export const BasicsStep: React.FC<BasicsStepProps> = ({
  draft,
  users,
  templateLocked,
  onApplyTemplate,
  onNext,
  onCancel,
}) => {
  const [uidStatus, setUidStatus] = useState<UidStatus>('idle');
  const [uidTouched, setUidTouched] = useState(!!draft.uid);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BasicsData>({
    resolver: zodResolver(basicsSchema),
    mode: 'onChange',
    defaultValues: {
      givenName: draft.givenName,
      sn: draft.sn,
      mail: draft.mail,
      uid: draft.uid,
    },
  });

  const givenName = watch('givenName');
  const sn = watch('sn');
  const uid = watch('uid');

  // Auto-suggest uid from the name until the field is edited manually
  useEffect(() => {
    if (!uidTouched && givenName && sn) {
      setValue('uid', suggestUid(givenName, sn), { shouldValidate: true });
    }
  }, [givenName, sn, uidTouched, setValue]);

  // Debounced availability check
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!uid) {
      setUidStatus('idle');
      return;
    }
    if (!UID_PATTERN.test(uid)) {
      setUidStatus('invalid');
      return;
    }
    setUidStatus('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await ldapUsersApi.checkUid(uid);
        if (res.available) {
          setUidStatus('available');
        } else {
          setUidStatus(
            res.reason === 'database' ? 'taken-db' : res.reason === 'invalid' ? 'invalid' : 'taken-ldap'
          );
        }
      } catch {
        setUidStatus('idle');
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [uid]);

  const uidIndicator = () => {
    switch (uidStatus) {
      case 'checking':
        return <small className="text-gray-500"><i className="pi pi-spinner pi-spin mr-1" />Checking availability...</small>;
      case 'available':
        return <small className="text-green-600"><i className="pi pi-check mr-1" />Available</small>;
      case 'taken-ldap':
        return <small className="text-red-500"><i className="pi pi-times mr-1" />Already taken in LDAP</small>;
      case 'taken-db':
        return <small className="text-red-500"><i className="pi pi-times mr-1" />Conflicts with an existing MemberHub account</small>;
      case 'invalid':
        return <small className="text-red-500"><i className="pi pi-times mr-1" />Invalid format</small>;
      default:
        return null;
    }
  };

  const activeUsers = users.filter((u) => u.status === 'active');
  const templateUser = activeUsers.find((u) => u.uid === draft.templateUid) || null;

  return (
    <div data-component="ldap-wizard-basics" className="flex flex-col gap-4">
      {!templateLocked && (
        <div className="border border-dashed border-gray-300 rounded-lg p-3">
          <label className="text-sm font-medium block mb-1">
            Start from existing user (optional)
          </label>
          <Dropdown
            value={templateUser}
            options={activeUsers}
            optionLabel="uid"
            onChange={(e) => onApplyTemplate(e.value ?? null)}
            placeholder="Select a user as template..."
            showClear
            filter
            className="w-full"
          />
        </div>
      )}
      {draft.templateUid && (
        <div className="flex items-center gap-2 text-sm bg-purple-50 border border-purple-200 rounded p-2">
          <i className="pi pi-copy text-purple-500" />
          <span>
            Template <strong>{draft.templateUid}</strong>: copied {draft.groups.length}{' '}
            {draft.groups.length === 1 ? 'group' : 'groups'} + posix settings. Name, email and
            password are not copied.
          </span>
          {!templateLocked && (
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm ml-auto"
              tooltip="Remove template"
              onClick={() => onApplyTemplate(null)}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium block mb-1">First Name *</label>
          <Controller
            name="givenName"
            control={control}
            render={({ field }) => (
              <InputText
                value={field.value}
                onChange={field.onChange}
                className={`w-full ${errors.givenName ? 'p-invalid' : ''}`}
                placeholder="Max"
              />
            )}
          />
          {errors.givenName && (
            <small className="text-red-500 mt-1 block">{errors.givenName.message}</small>
          )}
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Last Name *</label>
          <Controller
            name="sn"
            control={control}
            render={({ field }) => (
              <InputText
                value={field.value}
                onChange={field.onChange}
                className={`w-full ${errors.sn ? 'p-invalid' : ''}`}
                placeholder="Muster"
              />
            )}
          />
          {errors.sn && <small className="text-red-500 mt-1 block">{errors.sn.message}</small>}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Email *</label>
        <Controller
          name="mail"
          control={control}
          render={({ field }) => (
            <InputText
              value={field.value}
              onChange={field.onChange}
              className={`w-full ${errors.mail ? 'p-invalid' : ''}`}
              placeholder="max@linienriss.org"
            />
          )}
        />
        {errors.mail && <small className="text-red-500 mt-1 block">{errors.mail.message}</small>}
      </div>

      <div>
        <label className="text-sm font-medium block mb-1">Username (uid) *</label>
        <Controller
          name="uid"
          control={control}
          render={({ field }) => (
            <InputText
              value={field.value}
              onChange={(e) => {
                setUidTouched(true);
                field.onChange(e);
              }}
              className={`w-full font-mono ${errors.uid || uidStatus.startsWith('taken') ? 'p-invalid' : ''}`}
              placeholder="mmuster"
            />
          )}
        />
        <div className="mt-1 min-h-[1.25rem]">
          {errors.uid ? (
            <small className="text-red-500">{errors.uid.message}</small>
          ) : (
            uidIndicator()
          )}
        </div>
        <small className="text-gray-500">
          Cannot be changed later. Used for login on all LDAP services.
        </small>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button label="Cancel" className="p-button-text" onClick={onCancel} />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          disabled={uidStatus !== 'available'}
          onClick={handleSubmit(onNext)}
        />
      </div>
    </div>
  );
};
